import React, { useRef, useState, useCallback } from 'react';
import { detectFileRoute, detectTextRoute, describeRoute, IntakeRoute } from './hooks/useAutoDetectInput';

const BORDER_THIN = '1px solid #ddd';
const INPUT_BASE = {
  width: '100%',
  padding: '10px',
  border: BORDER_THIN,
  borderRadius: '6px',
  fontSize: '1rem',
} as const;

export const AUTO_CATEGORY_VALUE = '__auto__';

interface Props {
  categoryOptions: readonly string[];
  onSubmit: (route: IntakeRoute, category: string) => void;
  onInvalidFile?: (message: string) => void;
  fileSizeCaps: {
    pdf: { maxBytes: number; label: string };
    csv: { maxBytes: number; label: string };
    image: { maxBytes: number; label: string };
  };
  disabled?: boolean;
}

export const IntakeZone: React.FC<Props> = ({
  categoryOptions,
  onSubmit,
  onInvalidFile,
  fileSizeCaps,
  disabled = false,
}) => {
  const [category, setCategory] = useState<string>(AUTO_CATEGORY_VALUE);
  const [textInput, setTextInput] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): boolean => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
    const isImg = file.type.startsWith('image/');
    let cap: { maxBytes: number; label: string } | null = null;
    if (isPdf) cap = fileSizeCaps.pdf;
    else if (isCsv) cap = fileSizeCaps.csv;
    else if (isImg) cap = fileSizeCaps.image;
    if (cap && file.size > cap.maxBytes) {
      onInvalidFile?.(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is ${cap.label}.`);
      return false;
    }
    return true;
  }, [fileSizeCaps, onInvalidFile]);

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!validateFile(file)) return;
    setSelectedFile(file);
    setTextInput('');
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const currentRoute: IntakeRoute = selectedFile
    ? detectFileRoute(selectedFile)
    : detectTextRoute(textInput);

  const canSubmit = !disabled && currentRoute.kind !== 'empty';

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(currentRoute, category);
  };

  const clearFile = () => {
    setSelectedFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const routeLabel = describeRoute(currentRoute);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <label htmlFor="intake-category" style={{ fontWeight: 500, display: 'block', marginBottom: '4px' }}>
          Category:
        </label>
        <select
          id="intake-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{ ...INPUT_BASE }}
          disabled={disabled}
        >
          <option value={AUTO_CATEGORY_VALUE}>Auto-detect (recommended for bulk)</option>
          {categoryOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <p style={{ fontSize: '0.85rem', color: '#7f8c8d', margin: '4px 0 0' }}>
          Auto-detect classifies each product individually before generating descriptions.
          Pick a specific category to apply the same one to every product.
        </p>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => { if (!disabled && !selectedFile) fileRef.current?.click(); }}
        role="button"
        tabIndex={0}
        aria-label="Upload area: drop a file, paste text, or click to browse"
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !selectedFile) fileRef.current?.click(); }}
        style={{
          border: dragOver ? '2px dashed #3498db' : '2px dashed #bdc3c7',
          background: dragOver ? '#e3f2fd' : selectedFile ? '#ecf0f1' : '#f9f9f9',
          borderRadius: '10px',
          padding: '2rem',
          transition: 'all 0.15s ease',
          cursor: disabled ? 'not-allowed' : selectedFile ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,text/csv,image/*,.csv"
          onChange={(e) => handleFiles(e.target.files)}
          style={{ display: 'none' }}
        />

        <div style={{ textAlign: 'center', marginBottom: selectedFile ? '0.75rem' : '1rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.25rem' }} aria-hidden="true">📥</div>
          <p style={{ fontSize: '1.05rem', margin: 0, color: '#2c3e50', fontWeight: 500 }}>
            Drop a file, paste a URL, or type product details
          </p>
          <p style={{ fontSize: '0.85rem', color: '#7f8c8d', margin: '4px 0 0' }}>
            PDF · CSV · Image · URL (one per line for bulk)
          </p>
        </div>

        {selectedFile && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              border: BORDER_THIN,
              borderRadius: '6px',
              padding: '0.75rem 1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
              marginBottom: '0.75rem',
            }}
          >
            <span style={{ fontSize: '0.95rem', color: '#2c3e50' }}>
              📎 {selectedFile.name} <span style={{ color: '#7f8c8d' }}>
                ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            </span>
            <button
              onClick={clearFile}
              aria-label="Remove file"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                color: '#7f8c8d',
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}

        {!selectedFile && (
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={
              'Paste a product URL (one per line for multiple)\n' +
              'Or type product details directly…'
            }
            rows={6}
            disabled={disabled}
            style={{
              ...INPUT_BASE,
              background: 'white',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            padding: '12px 32px',
            border: 'none',
            borderRadius: '6px',
            background: canSubmit ? '#27ae60' : '#bdc3c7',
            color: '#fff',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          Generate →
        </button>
        {routeLabel && (
          <span
            aria-live="polite"
            style={{ fontSize: '0.9rem', color: '#7f8c8d' }}
          >
            Detected: {routeLabel}
          </span>
        )}
      </div>
    </div>
  );
};
